import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { useServices } from "@/app/use-services";
import { errorMessage } from "@/lib/api/errors";
import type { Friend } from "@/lib/api/models/social";
import { ErrorView } from "@/ui/ErrorView";
import { Page } from "@/ui/Page";
import { Body, Label, Tiny } from "@/ui/Text";
import { Button } from "@/ui/Button";
import { FRIENDS_QUERY_KEY, friendName } from "./friend-name";

function statusLine(friend: Friend): string {
  if (friend.status === "pending") return friend.outgoing ? "Invited · waiting on them" : "Wants to connect";
  if (friend.mutuals === 0) return "On Antar";
  return `On Antar · ${friend.mutuals} ${friend.mutuals === 1 ? "friend" : "friends"} in common`;
}

/**
 * The people already connected. The web asks for no address book — people
 * are found by handle, from a public page or an invitation — so the tab
 * says that rather than pretending to a contacts button it cannot honour.
 */
export function FriendsTab() {
  const { api, analytics } = useServices();
  const navigate = useNavigate();
  const friends = useQuery({ queryKey: FRIENDS_QUERY_KEY, queryFn: () => api.fetchFriends() });

  return (
    <Page>
      <Label>Friends</Label>
      {friends.isPending && <Body className="mt-3">Looking for people you know…</Body>}
      {friends.isError && <ErrorView message={errorMessage(friends.error)} onRetry={() => void friends.refetch()} />}
      {friends.data && friends.data.length === 0 && (
        <>
          <Body className="mt-3">Nobody you know is here yet. You get to be first.</Body>
          <Body className="mt-3 text-mid">Share your page, and anyone who opens it can connect with you. On the phone, Antar can also check your contacts — never here.</Body>
          <div className="mt-5"><Button label="Set up my page" outlined onClick={() => navigate("/you/page")} /></div>
        </>
      )}
      {friends.data && friends.data.length > 0 && (
        <ul className="mt-2">
          {friends.data.map((friend) => (
            <li key={friend.user_id ?? friend.username ?? statusLine(friend)}>
              <button
                type="button"
                onClick={() => {
                  if (!friend.user_id) return;
                  analytics.track({ name: "friend opened" });
                  navigate(`/friends/${friend.user_id}`);
                }}
                className="flex w-full items-center gap-3 border-b border-rule py-3 text-left"
              >
                <span aria-hidden className="h-[38px] w-[38px] shrink-0 rounded-full bg-rule" />
                <span className="min-w-0 flex-1">
                  <span className="block font-display text-[17px] leading-tight">{friendName(friend)}</span>
                  <span className="block text-[12.5px] text-mid">{statusLine(friend)}</span>
                </span>
                <span className="font-mono text-[10px] tracking-[0.12em]">{friend.status === "accepted" ? "VIEW" : "OPEN"}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <Tiny className="mt-5">Connections are by handle. We never see your address book on the web.</Tiny>
    </Page>
  );
}
